import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  DialogContentText,
  FormControlLabel,
  FormControl,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip as ChartTooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useTranslation } from 'react-i18next';
import type { SimulationRunSummary } from '../../../../types/simulation';
import type { SimulationRunSettings, FleetSettings, VehicleSettings, ResultsVehicleRow } from '../../../../types/simulation';
import {
  DEFAULT_FLEET_OPTIMISATION_VALUES,
  calculateFleetOptimisation,
  parseFleetOptimisationInputs,
  type FleetOptimisationInputs,
  type FleetOptimisationResult,
  type FleetOptimisationFormValues,
} from '../../../../services/fleetOptimisationCalculator';
import { fetchSimulationMetrics, fetchSimulationSettings, fetchSimulationVehicles } from '../../../../services/simulation/resultsApi';
import { downloadCostsAndStatisticsXlsx } from './costsAndStatisticsXlsx';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../../utils/iconFallbacks';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend, ChartDataLabels);

interface CostsAndStatisticsModalProps {
  open: boolean;
  onClose: () => void;
  selectedSim?: SimulationRunSummary | null;
  runId?: string | null;
  simulationOptions?: SimulationRunSummary[];
}

type CostBasis = 'daily' | 'monthly' | 'annual';
type CurrencyCode = 'GBP' | 'EUR' | 'USD' | 'AUD' | 'AED' | 'BRL';
type ComparisonSeriesRow = {
  runId: string;
  label: string;
  simulatedCost: number;
  baselineCost: number;
  savings: number;
  simulatedCo2: number;
  baselineCo2: number;
  co2Reduction: number;
};
type CalculatorResultsViewTab = 'visual' | 'metrics';
type VehicleTypeCostEntry = {
  key: string;
  label: string;
  vehicleCount: number;
  standardSeats: number;
  wheelchairSeats: number;
};
type SimulationVehicleCostProfile = {
  simulationLabel: string;
  entries: VehicleTypeCostEntry[];
  costsByType: Record<string, string>;
};

const NUMBER_FORMATTER = new Intl.NumberFormat('en-GB', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const BASIS_MULTIPLIER: Record<CostBasis, number> = {
  daily: 1,
  monthly: 20,
  annual: 250,
};

function computeEffectiveResults(
  inputs: FleetOptimisationInputs,
  includeCurrentOperations: boolean
): FleetOptimisationResult {
  if (includeCurrentOperations) {
    return calculateFleetOptimisation(inputs);
  }
  const simulatedOnlyInputs: FleetOptimisationInputs = {
    ...inputs,
    currentFleet: 0,
    baselineDailyKm: 0,
    baselineCapexEnabled: false,
    baselineCapexPerVehicle: 0,
    baselineVehiclesWithCapex: 0,
    analysisYears: 1,
    baselineDriverHoursEnabled: false,
    baselineDriverHoursPerVehicle: 0,
  };
  return calculateFleetOptimisation(simulatedOnlyInputs);
}

function formatVehicleTypeLabel(
  vehicle: VehicleSettings,
  t: import('i18next').TFunction<'simulation'>
): string {
  const standard = vehicle.vehicle.capacity.find((c) => c.type === 'standard')?.number ?? 0;
  const wheelchair = vehicle.vehicle.capacity.find((c) => c.type === 'wheelchair')?.number ?? 0;
  return t('results.costCalculator.standardWcLabel', { standard, wheelchair });
}

function extractFleetFromSettings(settings: SimulationRunSettings | null | undefined): FleetSettings | null {
  if (!settings) return null;

  const isFleetCandidate = (value: unknown): value is FleetSettings => {
    if (!value || typeof value !== 'object') return false;
    const maybeVehicles = (value as Record<string, unknown>).vehicles;
    if (!Array.isArray(maybeVehicles) || maybeVehicles.length === 0) return false;
    return maybeVehicles.every((entry) => {
      if (!entry || typeof entry !== 'object') return false;
      const e = entry as Record<string, unknown>;
      const count = e.number_of_vehicles;
      const vehicle = e.vehicle;
      if (typeof count !== 'number' || !vehicle || typeof vehicle !== 'object') return false;
      const capacity = (vehicle as Record<string, unknown>).capacity;
      return Array.isArray(capacity);
    });
  };

  const scanForFleet = (root: unknown): FleetSettings | null => {
    if (!root || typeof root !== 'object') return null;
    const queue: unknown[] = [root];
    const visited = new Set<object>();
    while (queue.length > 0) {
      const node = queue.shift();
      if (!node || typeof node !== 'object') continue;
      if (visited.has(node as object)) continue;
      visited.add(node as object);
      if (isFleetCandidate(node)) {
        return node;
      }
      if (Array.isArray(node)) {
        for (const item of node) queue.push(item);
      } else {
        for (const value of Object.values(node as Record<string, unknown>)) {
          queue.push(value);
        }
      }
    }
    return null;
  };

  const fromRuntime = scanForFleet(settings.simulation_settings);
  if (fromRuntime) return fromRuntime;
  const fromScenario = scanForFleet(settings.scenario);
  if (fromScenario) return fromScenario;
  const fromWholeSettings = scanForFleet(settings);
  if (fromWholeSettings) return fromWholeSettings;
  return null;
}

function buildVehicleTypeEntriesFromFleet(
  fleet: FleetSettings | null,
  defaultAnnualCost: string,
  t: import('i18next').TFunction<'simulation'>
): {
  entries: VehicleTypeCostEntry[];
  costsByType: Record<string, string>;
} {
  if (!fleet || !Array.isArray(fleet.vehicles)) {
    return { entries: [], costsByType: {} };
  }
  const entries = fleet.vehicles.map((vehicleSetting, index) => {
    const standardSeats = vehicleSetting.vehicle.capacity.find((c) => c.type === 'standard')?.number ?? 0;
    const wheelchairSeats = vehicleSetting.vehicle.capacity.find((c) => c.type === 'wheelchair')?.number ?? 0;
    const label = formatVehicleTypeLabel(vehicleSetting, t);
    const key = `${standardSeats}_${wheelchairSeats}__${index}`;
    return {
      key,
      label,
      vehicleCount: vehicleSetting.number_of_vehicles,
      standardSeats,
      wheelchairSeats,
    };
  });
  const costsByType = entries.reduce<Record<string, string>>((acc, entry) => {
    acc[entry.key] = defaultAnnualCost;
    return acc;
  }, {});
  return { entries, costsByType };
}

function createManualVehicleTypeEntry(index: number, defaultVehicleCount = 1): VehicleTypeCostEntry {
  return {
    key: `manual_${Date.now()}_${index}`,
    label: '',
    vehicleCount: Math.max(1, defaultVehicleCount),
    standardSeats: 0,
    wheelchairSeats: 0,
  };
}

function buildVehicleTypeEntriesFromVehicleRows(
  rows: ResultsVehicleRow[],
  defaultAnnualCost: string,
  t: import('i18next').TFunction<'simulation'>
): {
  entries: VehicleTypeCostEntry[];
  costsByType: Record<string, string>;
} {
  const byShift = new Map<string, { standardSeats: number; wheelchairSeats: number }>();
  rows.forEach((row) => {
    const shiftId = row.vehicle_shift_id ?? row.vehicle_id;
    if (!shiftId) return;
    const current = byShift.get(shiftId) ?? { standardSeats: 0, wheelchairSeats: 0 };
    const seats = typeof row.seats === 'number' && Number.isFinite(row.seats) ? row.seats : 0;
    const capacity = (row.capacity_type ?? '').toLowerCase();
    if (capacity.includes('wheelchair') || capacity === 'wc') {
      current.wheelchairSeats = seats;
    } else if (capacity.includes('standard')) {
      current.standardSeats = seats;
    }
    byShift.set(shiftId, current);
  });

  const grouped = new Map<string, { standardSeats: number; wheelchairSeats: number; vehicleCount: number }>();
  Array.from(byShift.values()).forEach((cfg) => {
    const key = `${cfg.standardSeats}_${cfg.wheelchairSeats}`;
    const existing = grouped.get(key) ?? { ...cfg, vehicleCount: 0 };
    existing.vehicleCount += 1;
    grouped.set(key, existing);
  });

  const entries = Array.from(grouped.entries()).map(([key, cfg], index) => ({
    key: `rows_${key}_${index}`,
    label: t('results.costCalculator.standardWcLabel', { standard: cfg.standardSeats, wheelchair: cfg.wheelchairSeats }),
    vehicleCount: cfg.vehicleCount,
    standardSeats: cfg.standardSeats,
    wheelchairSeats: cfg.wheelchairSeats,
  }));
  const costsByType = entries.reduce<Record<string, string>>((acc, entry) => {
    acc[entry.key] = defaultAnnualCost;
    return acc;
  }, {});
  return { entries, costsByType };
}

function computeWeightedAnnualFixedCost(
  profile: SimulationVehicleCostProfile | undefined,
  fallbackAnnualFixedCost: number
): number {
  if (!profile || profile.entries.length <= 1) return fallbackAnnualFixedCost;
  const totals = profile.entries.reduce(
    (acc, entry) => {
      const parsedCost = Number(profile.costsByType[entry.key]);
      if (!Number.isFinite(parsedCost) || parsedCost < 0) return acc;
      acc.totalVehicles += entry.vehicleCount;
      acc.totalCost += parsedCost * entry.vehicleCount;
      return acc;
    },
    { totalVehicles: 0, totalCost: 0 }
  );
  if (totals.totalVehicles <= 0) return fallbackAnnualFixedCost;
  return totals.totalCost / totals.totalVehicles;
}

function seedValues(selectedSim?: SimulationRunSummary | null): FleetOptimisationFormValues {
  const seeded = { ...DEFAULT_FLEET_OPTIMISATION_VALUES };
  const simulatedFleet = selectedSim?.no_vehicles;
  const totalKm = selectedSim?.total_km_travelled;
  const vehicleHours = selectedSim?.vehicle_hours;
  const allocatedVehicleShifts = selectedSim?.allocated_vehicle_shifts;
  const averageVehicleDistance = selectedSim?.average_vehicle_distance;
  if (typeof simulatedFleet === 'number' && simulatedFleet > 0) {
    seeded.currentFleet = String(simulatedFleet + 2);
    seeded.optimizedFleet = String(simulatedFleet);
    seeded.optimizedVehiclesWithCapex = String(simulatedFleet);
    if (typeof vehicleHours === 'number' && vehicleHours > 0) {
      seeded.optimizedDriverHoursPerVehicle = (vehicleHours / simulatedFleet).toFixed(2);
    }
    if (typeof averageVehicleDistance === 'number' && averageVehicleDistance > 0) {
      seeded.optimizedDailyKm = averageVehicleDistance.toFixed(2);
    } else if (
      typeof totalKm === 'number'
      && totalKm > 0
      && typeof allocatedVehicleShifts === 'number'
      && allocatedVehicleShifts > 0
    ) {
      const perVehicleDailyKm = totalKm / allocatedVehicleShifts;
      seeded.optimizedDailyKm = perVehicleDailyKm.toFixed(2);
    }
  }
  return seeded;
}

const COST_ASSUMPTION_INPUT_KEYS: Array<keyof FleetOptimisationFormValues> = [
  'costPerKm',
  'annualFixedCost',
  'optimizedCo2GramsPerKm',
  'driverCostPerHour',
];

const CURRENT_OPERATIONS_INPUT_KEYS: Array<keyof FleetOptimisationFormValues> = [
  'currentFleet',
  'baselineDailyKm',
  'baselineCo2GramsPerKm',
  'baselineDriverHoursPerVehicle',
];

function getCostAssumptionInputLabel(
  key: keyof FleetOptimisationFormValues,
  t: import('i18next').TFunction<'simulation'>
): string {
  switch (key) {
    case 'costPerKm': return t('results.costCalculator.inputs.costPerKm');
    case 'annualFixedCost': return t('results.costCalculator.inputs.annualFixedCost');
    case 'optimizedCo2GramsPerKm': return t('results.costCalculator.inputs.optimizedCo2');
    case 'driverCostPerHour': return t('results.costCalculator.inputs.driverCostPerHour');
    case 'currentFleet': return t('results.costCalculator.inputs.currentFleet');
    case 'baselineDailyKm': return t('results.costCalculator.inputs.baselineDailyKm');
    case 'baselineCo2GramsPerKm': return t('results.costCalculator.inputs.baselineCo2');
    case 'baselineDriverHoursPerVehicle': return t('results.costCalculator.inputs.baselineDriverHours');
    default: return String(key);
  }
}

const iconLabelSx = { fontSize: 16, color: 'text.secondary' } as const;

const CostsAndStatisticsModal: React.FC<CostsAndStatisticsModalProps> = ({
  open,
  onClose,
  selectedSim,
  runId,
  simulationOptions = [],
}) => {
  const { t } = useTranslation('simulation');
  const [values, setValues] = useState<FleetOptimisationFormValues>(() => seedValues(selectedSim));
  const [includeCurrentOperations, setIncludeCurrentOperations] = useState<boolean>(true);
  const [costBasis, setCostBasis] = useState<CostBasis>('daily');
  const [currencyCode, setCurrencyCode] = useState<CurrencyCode>('GBP');
  const [additionalRunIds, setAdditionalRunIds] = useState<string[]>([]);
  const [additionalRows, setAdditionalRows] = useState<ComparisonSeriesRow[]>([]);
  const [additionalMetricsByRunId, setAdditionalMetricsByRunId] = useState<Record<string, Partial<SimulationRunSummary>>>({});
  const [additionalLoading, setAdditionalLoading] = useState<boolean>(false);
  const [additionalError, setAdditionalError] = useState<string | null>(null);
  const [resultsViewTab, setResultsViewTab] = useState<CalculatorResultsViewTab>('visual');
  const [isVehicleCostModalOpen, setIsVehicleCostModalOpen] = useState<boolean>(false);
  const [vehicleCostProfiles, setVehicleCostProfiles] = useState<Record<string, SimulationVehicleCostProfile>>({});
  const driverHoursEnabled = values.baselineDriverHoursEnabled || values.optimizedDriverHoursEnabled;

  useEffect(() => {
    if (!open) return;
    setValues(seedValues(selectedSim));
    setIncludeCurrentOperations(true);
    setCostBasis('daily');
    setCurrencyCode('GBP');
    setAdditionalRunIds([]);
    setAdditionalRows([]);
    setAdditionalMetricsByRunId({});
    setAdditionalError(null);
    setResultsViewTab('visual');
    setIsVehicleCostModalOpen(false);
    setVehicleCostProfiles({});
  }, [open, selectedSim]);

  const parsed = useMemo(() => parseFleetOptimisationInputs(values), [values]);
  const basisMultiplier = BASIS_MULTIPLIER[costBasis];
  const basisLabel = useMemo(() => {
    switch (costBasis) {
      case 'daily':
        return t('results.costCalculator.basisDailyLabel');
      case 'monthly':
        return t('results.costCalculator.basisMonthlyLabel');
      case 'annual':
        return t('results.costCalculator.basisAnnualLabel');
    }
  }, [costBasis, t]);
  const CURRENCY_LABELS = useMemo<Record<CurrencyCode, string>>(
    () => ({
      GBP: t('results.costCalculator.currency.GBP'),
      EUR: t('results.costCalculator.currency.EUR'),
      USD: t('results.costCalculator.currency.USD'),
      AUD: t('results.costCalculator.currency.AUD'),
      AED: t('results.costCalculator.currency.AED'),
      BRL: t('results.costCalculator.currency.BRL'),
    }),
    [t]
  );
  const currentSimulationKey = selectedSim?.run_id ?? runId ?? 'selected';
  const comparedSimulationIds = useMemo(
    () => [currentSimulationKey, ...additionalRunIds],
    [currentSimulationKey, additionalRunIds]
  );

  useEffect(() => {
    if (!open || comparedSimulationIds.length === 0) return;
    let cancelled = false;
    const loadVehicleProfiles = async () => {
      const profilePairs = await Promise.all(
        comparedSimulationIds.map(async (simId) => {
          const summary = simId === currentSimulationKey
            ? selectedSim
            : simulationOptions.find((sim) => sim.run_id === simId);
          if (!summary) return [simId, null] as const;
          const resultsId = summary.results_id ?? summary.run_id;
          try {
            const vehicleRows = resultsId ? await fetchSimulationVehicles(resultsId) : [];
            const fromVehicleRows = buildVehicleTypeEntriesFromVehicleRows(vehicleRows, values.annualFixedCost, t);
            let entries = fromVehicleRows.entries;
            let costsByType = fromVehicleRows.costsByType;

            if (entries.length === 0) {
              const settings = resultsId ? await fetchSimulationSettings(resultsId) : null;
              const fleet = extractFleetFromSettings(settings);
              const fromFleet = buildVehicleTypeEntriesFromFleet(fleet, values.annualFixedCost, t);
              entries = fromFleet.entries;
              costsByType = fromFleet.costsByType;
            }

            const fallbackEntry = createManualVehicleTypeEntry(0, summary.no_vehicles ?? 1);
            return [simId, {
              simulationLabel: summary.scenario_name || summary.run_id,
              entries: entries.length > 0 ? entries : [fallbackEntry],
              costsByType: entries.length > 0
                ? costsByType
                : { [fallbackEntry.key]: values.annualFixedCost },
            } satisfies SimulationVehicleCostProfile] as const;
          } catch {
            return [simId, null] as const;
          }
        })
      );
      if (cancelled) return;
      setVehicleCostProfiles((prev) => {
        const next = { ...prev };
        for (const [simId, incoming] of profilePairs) {
          if (!incoming) {
            const existing = prev[simId];
            if (!existing || existing.entries.length === 0) {
              const defaultVehicleCount = simulationOptions.find((sim) => sim.run_id === simId)?.no_vehicles ?? selectedSim?.no_vehicles ?? 1;
              const fallbackEntry = createManualVehicleTypeEntry(0, defaultVehicleCount);
              next[simId] = {
                simulationLabel: simulationOptions.find((sim) => sim.run_id === simId)?.scenario_name || selectedSim?.scenario_name || simId,
                entries: [fallbackEntry],
                costsByType: {
                  [fallbackEntry.key]: values.annualFixedCost,
                },
              };
            }
            continue;
          }
          const existing = prev[simId];
          next[simId] = {
            simulationLabel: incoming.simulationLabel,
            entries: incoming.entries,
            costsByType: incoming.entries.reduce<Record<string, string>>((acc, entry) => {
              acc[entry.key] = existing?.costsByType[entry.key] ?? incoming.costsByType[entry.key];
              return acc;
            }, {}),
          };
        }
        return next;
      });
    };
    loadVehicleProfiles();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    comparedSimulationIds,
    currentSimulationKey,
    selectedSim,
    simulationOptions,
    values.annualFixedCost,
  ]);

  const effectiveResults = useMemo(() => {
    if (!parsed.inputs) return null;
    const weightedAnnualFixedCost = computeWeightedAnnualFixedCost(
      vehicleCostProfiles[currentSimulationKey],
      parsed.inputs.annualFixedCost
    );
    return computeEffectiveResults(
      { ...parsed.inputs, annualFixedCost: weightedAnnualFixedCost },
      includeCurrentOperations
    );
  }, [parsed.inputs, includeCurrentOperations, vehicleCostProfiles, currentSimulationKey]);

  useEffect(() => {
    if (!open || additionalRunIds.length === 0) {
      setAdditionalRows([]);
      setAdditionalError(null);
      return;
    }
    let cancelled = false;
    const loadAdditionalRows = async () => {
      setAdditionalLoading(true);
      setAdditionalError(null);
      try {
        const fetched = await Promise.all(
          additionalRunIds.map(async (selectedRunId) => {
            const base = simulationOptions.find((sim) => sim.run_id === selectedRunId);
            if (!base) return null;
            const metricsResultsId = base.results_id ?? base.run_id;
            const metrics = metricsResultsId ? await fetchSimulationMetrics(metricsResultsId).catch(() => null) : null;
            const mergedSummary = { ...base, ...(metrics ?? {}), results_id: base.results_id ?? metricsResultsId };
            const seeded = seedValues(mergedSummary);
            seeded.costPerKm = values.costPerKm;
            seeded.annualFixedCost = values.annualFixedCost;
            seeded.baselineCo2GramsPerKm = values.baselineCo2GramsPerKm;
            seeded.optimizedCo2GramsPerKm = values.optimizedCo2GramsPerKm;
            seeded.driverCostPerHour = values.driverCostPerHour;
            seeded.currentFleet = values.currentFleet;
            seeded.baselineDailyKm = values.baselineDailyKm;
            seeded.baselineDriverHoursPerVehicle = values.baselineDriverHoursPerVehicle;
            seeded.baselineDriverHoursEnabled = values.baselineDriverHoursEnabled;
            seeded.optimizedDriverHoursEnabled = values.optimizedDriverHoursEnabled;
            const parsedExtra = parseFleetOptimisationInputs(seeded);
            if (!parsedExtra.inputs) return null;
            const weightedAnnualFixedCost = computeWeightedAnnualFixedCost(
              vehicleCostProfiles[base.run_id],
              parsedExtra.inputs.annualFixedCost
            );
            const effectiveExtra = computeEffectiveResults(
              { ...parsedExtra.inputs, annualFixedCost: weightedAnnualFixedCost },
              includeCurrentOperations
            );
            return {
              runId: base.run_id,
              label: base.scenario_name || base.run_id,
              metrics: mergedSummary,
              simulatedCost: (effectiveExtra.optimizedCost / 250) * basisMultiplier,
              baselineCost: (effectiveExtra.baselineCost / 250) * basisMultiplier,
              savings: (effectiveExtra.financialSavings / 250) * basisMultiplier,
              simulatedCo2: effectiveExtra.optimizedCo2Tonnes,
              baselineCo2: effectiveExtra.baselineCo2Tonnes,
              co2Reduction: effectiveExtra.co2ReductionTonnes,
            } as ComparisonSeriesRow;
          })
        );
        if (cancelled) return;
        const validRows = fetched.filter((row): row is ComparisonSeriesRow & { metrics: Partial<SimulationRunSummary> } => row != null);
        setAdditionalRows(validRows);
        setAdditionalMetricsByRunId(
          validRows.reduce<Record<string, Partial<SimulationRunSummary>>>((acc, row) => {
            acc[row.runId] = row.metrics;
            return acc;
          }, {})
        );
      } catch {
        if (!cancelled) {
          setAdditionalRows([]);
          setAdditionalMetricsByRunId({});
          setAdditionalError(t('results.costCalculator.loadError'));
        }
      } finally {
        if (!cancelled) setAdditionalLoading(false);
      }
    };
    loadAdditionalRows();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    additionalRunIds,
    simulationOptions,
    includeCurrentOperations,
    basisMultiplier,
    vehicleCostProfiles,
  ]);

  const scaledOptimizedCost = effectiveResults ? effectiveResults.optimizedCost / 250 * basisMultiplier : null;
  const scaledBaselineCost = effectiveResults ? effectiveResults.baselineCost / 250 * basisMultiplier : null;
  const scaledSavings = effectiveResults ? effectiveResults.financialSavings / 250 * basisMultiplier : null;
  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [currencyCode]
  );

  const comparisonRows = useMemo(() => {
    if (!effectiveResults) return [];
    const primaryRunId = selectedSim?.run_id ?? runId ?? 'selected';
    const primaryLabel = selectedSim?.scenario_name || selectedSim?.run_id || runId || t('results.costCalculator.selectedFallback');
    const primary: ComparisonSeriesRow = {
      runId: primaryRunId,
      label: primaryLabel,
      simulatedCost: scaledOptimizedCost ?? 0,
      baselineCost: scaledBaselineCost ?? 0,
      savings: scaledSavings ?? 0,
      simulatedCo2: effectiveResults.optimizedCo2Tonnes,
      baselineCo2: effectiveResults.baselineCo2Tonnes,
      co2Reduction: effectiveResults.co2ReductionTonnes,
    };
    return [primary, ...additionalRows];
  }, [
    effectiveResults,
    additionalRows,
    selectedSim?.run_id,
    selectedSim?.scenario_name,
    runId,
    scaledOptimizedCost,
    scaledBaselineCost,
    scaledSavings,
    t,
  ]);

  const comparisonMetricsRows = useMemo(() => {
    const selectedMetrics = selectedSim ?? {};
    const toPercent = (value?: number) => (typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : '-');
    const toNumber = (value?: number) => (typeof value === 'number' ? NUMBER_FORMATTER.format(value) : '-');
    const toInt = (value?: number) => (typeof value === 'number' ? String(Math.round(value)) : '-');
    const baseRows = comparisonRows.map((row) => {
      const simSummary: Partial<SimulationRunSummary> = row.runId === (selectedSim?.run_id ?? runId)
        ? selectedMetrics
        : additionalMetricsByRunId[row.runId] ?? simulationOptions.find((sim) => sim.run_id === row.runId) ?? {};
      return {
        runId: row.runId,
        simulation: row.label,
        trips: toInt((simSummary.no_rides as number | undefined) ?? (simSummary.num_requests as number | undefined)),
        successRate: toPercent(simSummary.success_ratio as number | undefined),
        utilization: toNumber(simSummary.passenger_per_vehicle_hour as number | undefined),
        vehicleHours: toNumber(simSummary.vehicle_hours as number | undefined),
        medianWaitMin: toNumber(simSummary.waiting_time_min_median as number | undefined),
        fleetSize: toInt(simSummary.no_vehicles as number | undefined),
        cost: row.simulatedCost,
        co2: row.simulatedCo2,
      };
    });
    return baseRows;
  }, [
    comparisonRows,
    simulationOptions,
    selectedSim,
    runId,
    additionalMetricsByRunId,
  ]);

  const costChartData = useMemo(() => {
    if (comparisonRows.length === 0) return null;
    const simulatedData = comparisonRows.map((row) => row.simulatedCost);
    const labels = includeCurrentOperations
      ? [...comparisonRows.map((row) => row.label), t('results.costCalculator.currentOperationsLabel')]
      : comparisonRows.map((row) => row.label);
    const data = includeCurrentOperations
      ? [...simulatedData, scaledBaselineCost ?? 0]
      : simulatedData;
    const simBarColors = simulatedData.map((_value, index) =>
      index === 0 ? 'rgba(33, 150, 243, 0.75)' : 'rgba(33, 150, 243, 0.45)'
    );
    const backgroundColor = includeCurrentOperations
      ? [...simBarColors, 'rgba(244, 67, 54, 0.7)']
      : simBarColors;
    return {
      labels,
      datasets: [
        {
          label: t('results.costCalculator.basisCostDataset', { basis: basisLabel }),
          data,
          backgroundColor,
          borderRadius: 8,
        },
      ],
    };
  }, [comparisonRows, includeCurrentOperations, costBasis, scaledBaselineCost, t, basisLabel]);

  const co2ChartData = useMemo(() => {
    if (comparisonRows.length === 0) return null;
    const labels = includeCurrentOperations
      ? [...comparisonRows.map((row) => row.label), t('results.costCalculator.currentOperationsLabel')]
      : comparisonRows.map((row) => row.label);
    const simulatedData = comparisonRows.map((row) => row.simulatedCo2);
    const data = includeCurrentOperations
      ? [...simulatedData, (effectiveResults?.baselineCo2Tonnes ?? 0)]
      : simulatedData;
    const simBarColors = simulatedData.map((_value, index) =>
      index === 0 ? 'rgba(76, 175, 80, 0.75)' : 'rgba(76, 175, 80, 0.45)'
    );
    const backgroundColor = includeCurrentOperations
      ? [...simBarColors, 'rgba(255, 152, 0, 0.7)']
      : simBarColors;
    return {
      labels,
      datasets: [
        {
          label: t('results.costCalculator.annualCo2Dataset'),
          data,
          backgroundColor,
          borderRadius: 8,
        },
      ],
    };
  }, [comparisonRows, includeCurrentOperations, effectiveResults?.baselineCo2Tonnes, t]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        datalabels: {
          color: '#3A3A48',
          anchor: 'end' as const,
          align: 'top' as const,
          offset: 4,
          font: { size: 10, weight: 600 as const },
          formatter: (value: unknown) => {
            const numeric = typeof value === 'number' ? value : Number(value);
            if (!Number.isFinite(numeric)) return '';
            return NUMBER_FORMATTER.format(numeric);
          },
        },
      },
    }),
    []
  );

  const visualComparisonRows = useMemo(() => {
    if (!comparisonRows.length) return [];
    const rows = comparisonRows.map((row) => ({
      simulation: row.label,
      cost: row.simulatedCost,
      co2: row.simulatedCo2,
    }));
    if (includeCurrentOperations) {
      return [
        {
          simulation: t('results.costCalculator.currentOperationsLabel'),
          cost: scaledBaselineCost ?? 0,
          co2: effectiveResults?.baselineCo2Tonnes ?? 0,
        },
        ...rows,
      ];
    }
    return rows;
  }, [comparisonRows, includeCurrentOperations, scaledBaselineCost, effectiveResults?.baselineCo2Tonnes, t]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{t('results.costCalculator.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {t('results.costCalculator.intro')}
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={costBasis}
            onChange={(_, next: CostBasis | null) => {
              if (next) setCostBasis(next);
            }}
            aria-label={t('results.costCalculator.costBasisAria')}
          >
            <ToggleButton value="daily">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Icon size={20} name="calendar-day" style={iconLabelSx} />
                <span>{t('results.costCalculator.basisDaily')}</span>
              </Stack>
            </ToggleButton>
            <ToggleButton value="monthly">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Icon size={20} name="calendar-day" style={iconLabelSx} />
                <span>{t('results.costCalculator.basisMonthly')}</span>
              </Stack>
            </ToggleButton>
            <ToggleButton value="annual">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Icon size={20} name="calendar-day" style={iconLabelSx} />
                <span>{t('results.costCalculator.basisAnnual')}</span>
              </Stack>
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title={t('results.costCalculator.basisInfoTooltip')}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Icon name="circle-info" size={20} />
              <Typography variant="caption" color="text.secondary">
                {t('results.costCalculator.basisMultipliers')}
              </Typography>
            </Stack>
          </Tooltip>
          <FormControl size="small" sx={{ minWidth: 220, ml: { md: 'auto' } }}>
            <InputLabel id="simulation-cost-currency-label">{t('results.costCalculator.currencyLabel')}</InputLabel>
            <Select
              labelId="simulation-cost-currency-label"
              value={currencyCode}
              label={t('results.costCalculator.currencyLabel')}
              onChange={(event: SelectChangeEvent<CurrencyCode>) =>
                setCurrencyCode(event.target.value as CurrencyCode)
              }
            >
              {Object.entries(CURRENCY_LABELS).map(([code, label]) => (
                <MenuItem key={code} value={code}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Box sx={{ mb: 2 }}>
          <Autocomplete
            multiple
            size="small"
            options={simulationOptions.filter((sim) => sim.run_id !== (selectedSim?.run_id ?? runId))}
            value={simulationOptions.filter((sim) => additionalRunIds.includes(sim.run_id))}
            onChange={(_, sims) => setAdditionalRunIds(sims.map((sim) => sim.run_id))}
            getOptionLabel={(sim) => sim.scenario_name ? `${sim.scenario_name} (${sim.run_id})` : sim.run_id}
            isOptionEqualToValue={(option, value) => option.run_id === value.run_id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('results.costCalculator.additionalSimsLabel')}
                placeholder={t('results.costCalculator.additionalSimsPlaceholder')}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((sim, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={sim.run_id}
                  size="small"
                  label={sim.scenario_name || sim.run_id}
                  icon={<Icon size={20} d={ICON_FALLBACKS.Insights} />}
                />
              ))
            }
          />
          {additionalLoading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                {t('results.costCalculator.loadingExtra')}
              </Typography>
            </Stack>
          )}
          {additionalError && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {additionalError}
            </Alert>
          )}
        </Box>
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={resultsViewTab}
            onChange={(_event, value: CalculatorResultsViewTab) => setResultsViewTab(value)}
            sx={{ minHeight: 32 }}
          >
            <Tab value="visual" label={t('results.costCalculator.tabVisual')} sx={{ minHeight: 32 }} />
            <Tab value="metrics" label={t('results.costCalculator.tabMetrics')} sx={{ minHeight: 32 }} />
          </Tabs>
        </Box>

        {resultsViewTab === 'visual' && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                  <Icon size={20} name="bus" style={iconLabelSx} />
                  <Typography variant="subtitle2">
                    {t('results.costCalculator.costAssumptions')}
                  </Typography>
                </Stack>
                <Grid container spacing={1.5}>
                  {COST_ASSUMPTION_INPUT_KEYS
                    .filter((key) => driverHoursEnabled || key !== 'driverCostPerHour')
                    .map((key) => (
                    <Grid key={key} size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label={getCostAssumptionInputLabel(key, t)}
                        value={values[key] as string}
                        InputProps={key === 'optimizedCo2GramsPerKm' ? {
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon size={20} d={ICON_FALLBACKS.EnergySavingsLeaf} style={iconLabelSx} />
                            </InputAdornment>
                          ),
                        } : key === 'annualFixedCost' ? {
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button
                                size="small"
                                onClick={() => setIsVehicleCostModalOpen(true)}
                                sx={{ minWidth: 'auto', px: 1 }}
                              >
                                {t('results.costCalculator.split')}
                              </Button>
                            </InputAdornment>
                          ),
                        } : undefined}
                        onChange={(e) =>
                          setValues((prev) => ({
                            ...prev,
                            [key]: e.target.value,
                          }))
                        }
                        error={Boolean(parsed.errors[key as keyof typeof parsed.errors])}
                        helperText={
                          key === 'annualFixedCost'
                            ? (
                              parsed.errors[key as keyof typeof parsed.errors]
                                ?? (
                                  vehicleCostProfiles[currentSimulationKey]?.entries.length > 1
                                    ? t('results.costCalculator.splitAvailableHelper')
                                    : undefined
                                )
                            )
                            : parsed.errors[key as keyof typeof parsed.errors]
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
                <Stack spacing={0.5} sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={driverHoursEnabled}
                        onChange={(event) => setValues((prev) => ({
                          ...prev,
                          baselineDriverHoursEnabled: event.target.checked,
                          optimizedDriverHoursEnabled: event.target.checked,
                        }))}
                        size="small"
                      />
                    )}
                    label={t('results.costCalculator.driverHoursLabel', { state: driverHoursEnabled ? t('results.costCalculator.stateOn') : t('results.costCalculator.stateOff') })}
                    sx={{ mr: 0 }}
                  />
                </Stack>
                <Grid container spacing={1.5} sx={{ mt: 1 }}>
                  <Grid size={{ xs: 12, md: driverHoursEnabled ? 3 : 6 }}>
                    <Paper variant="outlined" sx={{ p: 1.25, height: '100%' }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('results.costCalculator.simulatedFleetSize')}
                      </Typography>
                      <Typography variant="h6">{values.optimizedFleet}</Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, md: driverHoursEnabled ? 3 : 6 }}>
                    <Paper variant="outlined" sx={{ p: 1.25, height: '100%' }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('results.costCalculator.simulatedDailyKm')}
                      </Typography>
                      <Typography variant="h6">{values.optimizedDailyKm}</Typography>
                    </Paper>
                  </Grid>
                  {driverHoursEnabled && (
                    <Grid size={{ xs: 12, md: 3 }}>
                      <Paper variant="outlined" sx={{ p: 1.25, height: '100%' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {t('results.costCalculator.driverHoursPerVehicle')}
                        </Typography>
                        <Typography variant="h6">{values.optimizedDriverHoursPerVehicle}</Typography>
                      </Paper>
                    </Grid>
                  )}
                  {driverHoursEnabled && (
                    <Grid size={{ xs: 12, md: 3 }}>
                      <Paper variant="outlined" sx={{ p: 1.25 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('results.costCalculator.totalDailyDriverHours')}
                        </Typography>
                        <Typography variant="h6">
                          {NUMBER_FORMATTER.format(
                            (Number(values.optimizedFleet) || 0) * (Number(values.optimizedDriverHoursPerVehicle) || 0)
                          )}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Icon size={20} name="bus" style={iconLabelSx} />
                    <Typography variant="subtitle2">{t('results.costCalculator.currentOperations')}</Typography>
                  </Stack>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeCurrentOperations}
                        onChange={(event) => setIncludeCurrentOperations(event.target.checked)}
                        size="small"
                      />
                    }
                    label={includeCurrentOperations ? t('results.costCalculator.stateOn') : t('results.costCalculator.stateOff')}
                    sx={{ mr: 0 }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  {t('results.costCalculator.currentOperationsHint')}
                </Typography>
                <Grid container spacing={1.5}>
                  {CURRENT_OPERATIONS_INPUT_KEYS
                    .filter((key) => driverHoursEnabled || key !== 'baselineDriverHoursPerVehicle')
                    .map((key) => (
                    <Grid key={key} size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label={getCostAssumptionInputLabel(key, t)}
                        value={values[key] as string}
                        InputProps={key === 'baselineCo2GramsPerKm' ? {
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon size={20} d={ICON_FALLBACKS.EnergySavingsLeaf} style={iconLabelSx} />
                            </InputAdornment>
                          ),
                        } : undefined}
                        disabled={!includeCurrentOperations}
                        onChange={(e) =>
                          setValues((prev) => ({
                            ...prev,
                            [key]: e.target.value,
                          }))
                        }
                        error={includeCurrentOperations && Boolean(parsed.errors[key as keyof typeof parsed.errors])}
                        helperText={!includeCurrentOperations ? '' : parsed.errors[key as keyof typeof parsed.errors]}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        )}

        {effectiveResults && resultsViewTab === 'visual' && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
              <Chip
                size="small"
                icon={<Icon size={20} d={ICON_FALLBACKS.Insights} />}
                label={includeCurrentOperations ? t('results.costCalculator.comparisonMode') : t('results.costCalculator.simulatedCostOnly')}
              />
              <Chip size="small" icon={<Icon size={20} name="calendar-day" />} variant="outlined" label={basisLabel} />
            </Stack>

            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Icon size={20} d={ICON_FALLBACKS.PaymentsOutlined} style={iconLabelSx} />
                    <Typography variant="caption" color="text.secondary">
                      {t('results.costCalculator.simulatedFleetCost', { basis: basisLabel })}
                    </Typography>
                  </Stack>
                  <Typography variant="h6">{currencyFormatter.format(scaledOptimizedCost ?? 0)}</Typography>
                </Paper>
              </Grid>
              {includeCurrentOperations && (
                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Icon size={20} d={ICON_FALLBACKS.PaymentsOutlined} style={iconLabelSx} />
                      <Typography variant="caption" color="text.secondary">
                        {t('results.costCalculator.currentOperationsCost', { basis: basisLabel })}
                      </Typography>
                    </Stack>
                    <Typography variant="h6">{currencyFormatter.format(scaledBaselineCost ?? 0)}</Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>

            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              <Grid size={{ xs: 12, md: includeCurrentOperations ? 6 : 12 }}>
                <Paper variant="outlined" sx={{ p: 1.5, height: 300 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                    <Icon size={20} d={ICON_FALLBACKS.PaymentsOutlined} style={iconLabelSx} />
                    <Typography variant="subtitle2">
                      {t('results.costCalculator.costComparisonHeading', { basis: basisLabel })}
                    </Typography>
                  </Stack>
                  {costChartData && <Bar data={costChartData} options={chartOptions} />}
                </Paper>
              </Grid>
              {co2ChartData && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper variant="outlined" sx={{ p: 1.5, height: 300 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                      <Icon size={20} d={ICON_FALLBACKS.EnergySavingsLeaf} style={iconLabelSx} />
                      <Typography variant="subtitle2">
                        {t('results.costCalculator.annualCo2Comparison')}
                      </Typography>
                    </Stack>
                    <Bar data={co2ChartData} options={chartOptions} />
                  </Paper>
                </Grid>
              )}
            </Grid>
            <Accordion sx={{ mt: 1.5 }}>
              <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
                <Typography variant="subtitle2">{t('results.costCalculator.comparisonTable')}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('results.costCalculator.simulationCol')}</TableCell>
                        <TableCell align="right">{t('results.costCalculator.basisCostCol', { basis: basisLabel })}</TableCell>
                        <TableCell align="right">{t('results.costCalculator.annualCo2Col')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visualComparisonRows.map((row) => (
                        <TableRow key={row.simulation}>
                          <TableCell>{row.simulation}</TableCell>
                          <TableCell align="right">{currencyFormatter.format(row.cost)}</TableCell>
                          <TableCell align="right">{NUMBER_FORMATTER.format(row.co2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}

        {effectiveResults && resultsViewTab === 'metrics' && (
          <Box sx={{ mt: 1 }}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('results.costCalculator.simulationCol')}</TableCell>
                    <TableCell align="right">{t('results.costCalculator.tripsCol')}</TableCell>
                    <TableCell align="right">{t('results.costCalculator.successRateCol')}</TableCell>
                    <TableCell align="right">{t('results.costCalculator.utilizationCol')}</TableCell>
                    <TableCell align="right">{t('results.costCalculator.vehicleHoursCol')}</TableCell>
                    <TableCell align="right">{t('results.costCalculator.medianWaitCol')}</TableCell>
                    <TableCell align="right">{t('results.costCalculator.fleetSizeCol')}</TableCell>
                    <TableCell align="right">{t('results.costCalculator.basisCostCol', { basis: basisLabel })}</TableCell>
                    <TableCell align="right">{t('results.costCalculator.annualCo2Col')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparisonMetricsRows.map((row) => (
                    <TableRow key={row.runId}>
                      <TableCell>{row.simulation}</TableCell>
                      <TableCell align="right">{row.trips}</TableCell>
                      <TableCell align="right">{row.successRate}</TableCell>
                      <TableCell align="right">{row.utilization}</TableCell>
                      <TableCell align="right">{row.vehicleHours}</TableCell>
                      <TableCell align="right">{row.medianWaitMin}</TableCell>
                      <TableCell align="right">{row.fleetSize}</TableCell>
                      <TableCell align="right">{currencyFormatter.format(row.cost)}</TableCell>
                      <TableCell align="right">{NUMBER_FORMATTER.format(row.co2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        <Dialog
          open={isVehicleCostModalOpen}
          onClose={() => setIsVehicleCostModalOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>{t('results.costCalculator.vehicleCostsModalTitle')}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 1.5 }}>
              {t('results.costCalculator.vehicleCostsModalIntro')}
            </DialogContentText>
            <Stack spacing={2}>
              {comparedSimulationIds.map((simId) => {
                const profile = vehicleCostProfiles[simId];
                if (!profile || profile.entries.length === 0) return null;
                return (
                  <Paper key={simId} variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {profile.simulationLabel}
                    </Typography>
                    <List dense sx={{ p: 0 }}>
                      {profile.entries.map((entry, entryIndex) => (
                        <ListItem key={entry.key} sx={{ px: 0, py: 0.75, alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1, minWidth: 0, pr: 1.5 }}>
                            <ListItemText
                              primary={(
                                <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
                                  <Stack direction="row" spacing={0.4} alignItems="center">
                                    <Icon size={15} d={ICON_FALLBACKS.EventSeat} style={{ color: '#52565e' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {entry.standardSeats}
                                    </Typography>
                                  </Stack>
                                  <Stack direction="row" spacing={0.4} alignItems="center">
                                    <Icon size={15} name="wheelchair-move" style={{ color: '#52565e' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {entry.wheelchairSeats}
                                    </Typography>
                                  </Stack>
                                  <Typography variant="body2" color="text.secondary">
                                    {entry.label || t('results.costCalculator.standardWcLabel', { standard: entry.standardSeats, wheelchair: entry.wheelchairSeats })}
                                  </Typography>
                                </Stack>
                              )}
                              secondary={t('results.costCalculator.vehicleCount', { count: entry.vehicleCount })}
                            />
                            {(entry.label === '' || profile.entries.length === 1) && (
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 0.75 }}>
                                <TextField
                                  size="small"
                                  type="number"
                                  label={t('results.costCalculator.standardSeats')}
                                  value={entry.standardSeats}
                                  onChange={(event) => {
                                    const nextStandard = Math.max(0, Number(event.target.value) || 0);
                                    setVehicleCostProfiles((prev) => {
                                      const current = prev[simId];
                                      if (!current) return prev;
                                      const nextEntries = [...current.entries];
                                      const target = nextEntries[entryIndex];
                                      nextEntries[entryIndex] = {
                                        ...target,
                                        standardSeats: nextStandard,
                                        label: t('results.costCalculator.standardWcLabel', { standard: nextStandard, wheelchair: target.wheelchairSeats }),
                                      };
                                      return { ...prev, [simId]: { ...current, entries: nextEntries } };
                                    });
                                  }}
                                  sx={{ width: 150 }}
                                />
                                <TextField
                                  size="small"
                                  type="number"
                                  label={t('results.costCalculator.wcSeats')}
                                  value={entry.wheelchairSeats}
                                  onChange={(event) => {
                                    const nextWc = Math.max(0, Number(event.target.value) || 0);
                                    setVehicleCostProfiles((prev) => {
                                      const current = prev[simId];
                                      if (!current) return prev;
                                      const nextEntries = [...current.entries];
                                      const target = nextEntries[entryIndex];
                                      nextEntries[entryIndex] = {
                                        ...target,
                                        wheelchairSeats: nextWc,
                                        label: t('results.costCalculator.standardWcLabel', { standard: target.standardSeats, wheelchair: nextWc }),
                                      };
                                      return { ...prev, [simId]: { ...current, entries: nextEntries } };
                                    });
                                  }}
                                  sx={{ width: 130 }}
                                />
                                <TextField
                                  size="small"
                                  type="number"
                                  label={t('results.costCalculator.vehicleCount')}
                                  value={entry.vehicleCount}
                                  onChange={(event) => {
                                    const nextCount = Math.max(1, Number(event.target.value) || 1);
                                    setVehicleCostProfiles((prev) => {
                                      const current = prev[simId];
                                      if (!current) return prev;
                                      const nextEntries = [...current.entries];
                                      nextEntries[entryIndex] = {
                                        ...nextEntries[entryIndex],
                                        vehicleCount: nextCount,
                                      };
                                      return { ...prev, [simId]: { ...current, entries: nextEntries } };
                                    });
                                  }}
                                  sx={{ width: 130 }}
                                />
                              </Stack>
                            )}
                          </Box>
                          <TextField
                            size="small"
                            label={t('results.costCalculator.annualFixedCost')}
                            value={profile.costsByType[entry.key] ?? ''}
                            onChange={(event) => {
                              const value = event.target.value;
                              setVehicleCostProfiles((prev) => ({
                                ...prev,
                                [simId]: {
                                  ...profile,
                                  costsByType: {
                                    ...profile.costsByType,
                                    [entry.key]: value,
                                  },
                                },
                              }));
                            }}
                            sx={{ width: 220 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                );
              })}
              {comparedSimulationIds.every((simId) => (vehicleCostProfiles[simId]?.entries.length ?? 0) <= 1) && (
                <Alert severity="info">
                  {t('results.costCalculator.noVehicleSeating')}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsVehicleCostModalOpen(false)}>{t('results.costCalculator.done')}</Button>
          </DialogActions>
        </Dialog>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('results.costCalculator.close')}</Button>
        <Button
          variant="contained"
          disabled={!parsed.inputs || !effectiveResults}
          onClick={() => {
            if (!parsed.inputs || !effectiveResults) return;
            downloadCostsAndStatisticsXlsx(parsed.inputs, effectiveResults, runId, {
              includeCurrentOperations,
              costBasis,
              costMultiplier: basisMultiplier,
              currencyCode,
              visualComparisonRows,
              metricsComparisonRows: comparisonMetricsRows,
            });
          }}
        >
          {t('results.costCalculator.exportXlsx')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CostsAndStatisticsModal;

